import { useEffect } from 'react'
import {
  clearCheckoutStepBreadcrumb,
  readAndClearCheckoutStepBreadcrumb,
} from '@mc/checkout'
import {
  CHECKOUT_BACK_NAV_TYPES,
  trackCheckoutBackExit,
} from '@mc/pre-paywall-events'

type CheckoutBackNavType =
  (typeof CHECKOUT_BACK_NAV_TYPES)[keyof typeof CHECKOUT_BACK_NAV_TYPES]

// Optional access throughout: this is analytics on a top-of-funnel page, so a
// missing Performance Timeline API must degrade to "not measured", never throw.
const isBackForwardNavigation = () => {
  const entries = window.performance?.getEntriesByType?.('navigation') ?? []
  const [entry] = entries as PerformanceNavigationTiming[]

  return entry?.type === 'back_forward'
}

const reportBackExit = (navType: CheckoutBackNavType) => {
  const breadcrumb = readAndClearCheckoutStepBreadcrumb()
  if (!breadcrumb) return

  trackCheckoutBackExit({
    originStep: breadcrumb.step,
    navType,
    isGift: breadcrumb.isGift,
  })
}

// Measures how often browser back drops a user out of checkout onto the plans
// page, and which step they were on. Detected on arrival rather than on exit
// because the exit unloads the document, and the tracking transport is not
// unload-safe.
export const useTrackCheckoutBackExit = () => {
  useEffect(() => {
    if (isBackForwardNavigation()) {
      reportBackExit(CHECKOUT_BACK_NAV_TYPES.BACK_FORWARD)
    } else {
      // Any other arrival means a breadcrumb left over from an earlier visit is
      // stale. Clearing it unconditionally keeps a later, unrelated back
      // navigation from reporting a step the user is no longer coming from.
      clearCheckoutStepBreadcrumb()
    }

    // A bfcache restore reuses the existing document, so it fires no navigation
    // and re-runs no effect — it needs its own listener.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return

      reportBackExit(CHECKOUT_BACK_NAV_TYPES.BFCACHE)
    }

    window.addEventListener('pageshow', onPageShow)

    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])
}
